import type { CatalogEntry } from "../types";

/**
 * Email-accounts (feature `02`) error codes + catalog.
 *
 * Code shape is `02AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * `Shared` codes (`0200xx`, AA=00) are recurring cross-endpoint conditions —
 * `AccountNotFound`, `OrgNotFound`, `PlanLimitReached`, `EmailAlreadyConnected` —
 * collapsed to ONE code each so the same condition reads the same everywhere.
 *
 * Endpoint → `AA` map (28-endpoint inventory):
 *   authorizeGmail=01, gmailCallback=02, authorizeGWS=03, gwsCallback=04,
 *   authorizeGWSAdmin=05, gwsAdminCallback=06, listGWSMailboxes=07,
 *   batchConnectGWS=08, authorizeOutlook=09, outlookCallback=10, testSmtp=11,
 *   connectSmtp=12, bulkConnect=13, listAccounts=15,
 *   bulkEnableWarmup=16, bulkPauseWarmup=17, bulkDelete=18, getAccount=19,
 *   deleteAccount=20, reconnect=21, getWarmupSettings=22, updateWarmupSettings=23,
 *   enableWarmup=24, pauseWarmup=25, checkDns=26, triggerVerification=27,
 *   getPoolInfo=28, getAccountOverview=29, getSmtpConfig=30, updateSmtp=31,
 *   startBulkOperation=32, getBulkJobStatus=32 (shared BulkOps framework),
 *   disconnect=33, microsoftPartnerConnect=34, partnerConnectOAuth=35.
 *
 * `getSmtpConfig`/`updateSmtp` gate on authMethod !== PASSWORD (not provider —
 * a GMAIL account connected via app password is also password-authenticated)
 * and reuse the Shared `NotSmtpAccount` code for the wrong-auth-method case
 * (one condition, one code).
 *
 * Dynamic detail (SMTP/IMAP failure reason, plan-limit max, retry minutes) is
 * folded into the user message at the throw site via `AppException`'s
 * `messageOverride`; the catalog message below is the BASE prose. For
 * `TestSmtp.{Smtp,Imap}Failed` that override is a SHORT, bounded sentence keyed
 * off a `ProbeFailureCause` — never the raw nodemailer/imapflow error, which is
 * unbounded, leaks internals, and carries provider URLs + session ids. The raw
 * string goes in `logContext` instead (11-conventions §6). The DNS gate
 * (`EnableWarmup.DnsCheckRequired`) is PROSE ONLY — per-record spf/dkim/dmarc/mx
 * flags are never carried in the error (the frontend reads them off the account
 * resource).
 */
export const EMAIL_ACCOUNTS_CODES = {
  /** Cross-endpoint conditions (AA=00). */
  Shared: {
    OrgNotFound: "020001",
    PlanLimitReached: "020002",
    EmailAlreadyConnected: "020003",
    AccountNotFound: "020004",
    NotSmtpAccount: "020005",
    BulkActionInProgress: "020006",
    // Distinct from `PlanLimitReached` (020002) on purpose. That one is the
    // CONNECT-time cap ("you can't add another mailbox — upgrade to add more");
    // this one is the RE-ENABLE cap ("every slot your plan allows is already
    // warming"). The remedy differs — pause another mailbox, or upgrade — so
    // overloading 020002 would send the user to the wrong action. Thrown by
    // enableWarmup / resumeWarmup / the bulk enable op, hence AA=00.
    WarmupAccountLimitReached: "020007",
    // The address's domain is on `BLOCKED_EMAIL_DOMAINS` (@trulyinbox/types).
    // AA=00 because several connect paths share this one condition and it must
    // read the same at each: the OAuth callbacks (gmail/GWS/outlook, via
    // `finalizeOAuthConnect`), partner Microsoft, `connectSmtp`, the Email-Infra
    // rename, and the CSV import's per-row error column.
    //
    // The three batch importers report it per row instead of throwing, so one
    // blocked address never aborts a batch: the CSV import's `invalid[]`, the
    // Saleshandy sync's `skippedEmails` (the PERMANENT bucket — a blocked domain
    // never becomes connectable, so the caller must advance its cursor past it),
    // and the DWD sync job's `failures[]`.
    //
    // Deliberately NOT folded into `EmailAlreadyConnected` (020003) or
    // `PlanLimitReached` (020002): those are "try a different mailbox" and
    // "upgrade". This one has no self-service remedy at all, so it must be
    // distinguishable in support tickets and in the CSV's per-row error column.
    BlockedEmailDomain: "020008",
    // The org's subscription is PAUSED, so NO mailbox can be connected OR
    // reconnected — every single-mailbox connect (the three OAuth authorize +
    // callback pairs, partner Microsoft, partner OAuth, SMTP), the CSV import,
    // and the reconnect branch of each OAuth callback. Not `PlanLimitReached`
    // (020002): the remedy is "resume your subscription", which an upgrade
    // doesn't deliver.
    //
    // ONE condition, TWO codes. `Billing.Shared.SubscriptionPaused` (040006) is
    // the general view-only gate and is what NEW call sites throw; this one is
    // kept because it has already reached an environment and a client may
    // branch on it. Both map to public `subscription_paused`, and every
    // consumer that branches on "paused" must accept both.
    SubscriptionPaused: "020009",
  },
  /** Connect callbacks (state decrypt + token exchange). AA=02 (canonical OAuth callback). */
  OauthCallback: {
    StateInvalid: "020201",
    StateExpired: "020202",
    TokenExchangeFailed: "020203",
    MissingRefreshToken: "020204",
    ProfileFetchFailed: "020205",
    /**
     * The provider authoritatively rejected the access token we were just
     * handed. Distinct from `TokenExchangeFailed` (the exchange itself failed)
     * and `ProfileFetchFailed` (the profile call failed for any reason) — this
     * one means the grant is already dead at connect time, so the remedy is to
     * re-authorize with permissions granted, not to retry.
     */
    GrantNotUsable: "020206",
  },
  /**
   * Headless OAuth connect for a partner-managed mailbox (AA=35) —
   * `POST /v2/workspaces/:workspaceId/accounts/oauth`. The partner already ran
   * their own consent flow and hands us a live access token; there is no
   * redirect and no code exchange, so the `OauthCallback` conditions above do
   * not apply. `GrantNotUsable` (`020206`) IS reused for a provider-rejected
   * token — same condition, same remedy, one code.
   *
   * **AA=35, not 34.** This block and `MicrosoftPartnerConnect` below were built
   * in parallel and both first claimed 34; that one shipped first, so it keeps
   * the number. Never renumber a code that has left this repo — a code is the
   * stable identity a client branches on, and reusing 34 for a different
   * condition is strictly worse than an out-of-order block.
   */
  PartnerConnect: {
    /** The caller's org has no ACTIVE partner integration registered. */
    PartnerNotConfigured: "023501",
    /** The provider is not one of the four OAuth mailbox providers. */
    ProviderUnsupported: "023502",
    /**
     * The provider's own profile endpoint reports a different mailbox than the
     * one the caller named. The token, not the payload, decides which mailbox
     * this is — accepting the caller's word would let one partner customer's
     * token be filed under another's address.
     */
    EmailMismatch: "023503",
    /**
     * `expiresAt` is already inside the 5-minute early-refresh window, so the
     * very first send would bounce straight back to the partner's refresh
     * endpoint. Reject at the door rather than store a token born expired.
     */
    ExpiryTooSoon: "023504",
  },
  /** Google Workspace admin OAuth + directory listing. */
  GwsAdmin: {
    StateInvalid: "020601",
    NotAuthenticated: "020701",
    DirectoryListFailed: "020702",
  },
  /** testSmtp=11 (SMTP/IMAP test with save → connectSmtp shares these via override). */
  TestSmtp: {
    SmtpFailed: "021101",
    ImapFailed: "021102",
    HostNotAllowed: "021103",
  },
  /** connectSmtp=12 (ESP-mismatch validation on the picked provider tile). */
  ConnectSmtp: {
    EspMismatch: "021201",
  },
  /** bulkConnect=13 (CSV upload validation + import-status lookup). */
  BulkConnect: {
    FileMustBeCsv: "021301",
    FileTooLarge: "021302",
    TooManyRows: "021303",
    ImportNotFound: "021304",
    NotMultipart: "021305",
    NoFileUploaded: "021306",
  },
  /** reconnect=21. */
  Reconnect: {
    AlreadyConnected: "022101",
    EmailMismatch: "022102",
    MissingSmtpFields: "022103",
  },
  /**
   * microsoftPartnerConnect=34 — the single-mailbox Microsoft consent link
   * (`.../accounts/microsoft/consent-url`) and its unguarded callback.
   *
   * Only `NotConfigured` ever reaches a caller as an HTTP error: it is thrown
   * on the consent-url call, which is a normal guarded `/v2` request. The other
   * three are CALLBACK outcomes, and the callback answers Microsoft's browser
   * redirect — it must always 302 to a result page, never render an error
   * envelope at someone's customer. They exist so the failure is named once,
   * logged by code, and mapped to a stable `partnerConnectReason` rather than
   * being re-described as a free-text string at each branch.
   */
  MicrosoftPartnerConnect: {
    NotConfigured: '023401',
    SessionInvalidOrExpired: '023402',
    EmailMismatch: '023403',
    ConsentDenied: '023404',
  },
  /** updateWarmupSettings=23. */
  UpdateWarmupSettings: {
    WindowCrossesMidnight: "022301",
    WindowTooShort: "022302",
    ExceedsPlanLimit: "022303",
    EspMustSum100: "022304",
    CustomEspRequiresUpgrade: "022305",
    InsufficientDailyWindow: "022306",
    ReplyRateExceedsPlanLimit: "022307",
    // One code per gated settings section (not one shared "upgrade required"),
    // so the UI can route each denial to the right upsell.
    CustomContentRequiresUpgrade: "022308",
    TemplatesRequireUpgrade: "022309",
    CustomScheduleRequiresUpgrade: "022310",
    CustomLanguageRequiresUpgrade: "022311",
    ScheduleConflict: "022312",
    // Decision 16: the reply-rate SECTION is locked on `free` only. Distinct
    // from `ReplyRateExceedsPlanLimit` (022307), which is the NUMERIC clamp
    // (`replyRateCap`) applied on plans that DO hold the section — one says
    // "upgrade to use this", the other "that number is above your ceiling", and
    // collapsing them would send a paying customer to the pricing page.
    ReplyRateRequiresUpgrade: "022313",
  },
  /** enableWarmup=24. */
  EnableWarmup: {
    ExceedsPlanLimit: "022401",
    Disconnected: "022402",
    AlreadyRunning: "022403",
    DnsCheckRequired: "022404",
    NoScheduleDays: "022405",
    Suspended: "022406",
    // Transient: accountState hasn't caught up to CONNECTED yet after a
    // reconnect. Distinct from `Disconnected` so callers can retry instead
    // of telling the user to reconnect again.
    ReconnectPending: "022407",
    /**
     * The mailbox has not yet proved it can send and receive. A verification
     * run has just been started (or one is already in flight); warmup begins by
     * itself the moment it passes, so the client should surface progress rather
     * than an action.
     *
     * Deliberately DISTINCT from `MailboxVerificationFailed` below despite both
     * meaning "not verified": this one is transient and needs no user action,
     * that one needs the mailbox fixed. Collapsing them would tell someone to
     * go debug a mailbox that is simply still being checked.
     *
     * Also deliberately distinct from `DnsCheckRequired` (`022404`). Both are
     * gates on the enable path, but they name different failures with different
     * remedies — DNS records vs the mailbox itself — and a client that conflates
     * them sends the user to their DNS provider over a mailbox problem.
     */
    MailboxVerificationPending: "022408",
    /**
     * The mailbox failed verification: a probe hard-bounced, or one never
     * arrived across three checks. Warmup cannot start until the mailbox is
     * fixed and re-verified.
     */
    MailboxVerificationFailed: "022409",
  },
  /** pauseWarmup=25. */
  PauseWarmup: {
    NotRunning: "022501",
  },
  /** resumeWarmup=26. */
  ResumeWarmup: {
    NotSuspended: "022601",
    /**
     * Stopped, but not by anything a user may lift: a rate-driven bounce/spam
     * suspension, whose evidence is a rolling 7-day window that a resume cannot
     * clear (see `isUserResumable`). Deliberately DISTINCT from `NotSuspended`
     * above, whose remedy differs — that one means "there is nothing to resume",
     * this one means "there is, and it recovers on its own schedule".
     */
    NotUserResumable: "022602",
    /**
     * Resumable, but a required DNS record (SPF/MX, plus DKIM on a custom
     * domain) is not currently passing — so resuming would put the mailbox back
     * to sending on a domain the nightly gate will suspend again within a day.
     *
     * Deliberately DISTINCT from `EnableWarmup.DnsCheckRequired` (`022404`)
     * despite the identical remedy: the two are raised by different endpoints
     * against different account states, and a client that treats a resume
     * failure as an enable failure will offer the wrong control. Same reasoning
     * as `NotSuspended` vs `NotUserResumable` above.
     *
     * The remedy is a Re-check DNS (`POST /email-accounts/:id/dns/check`)
     * followed by a retry — resume deliberately does NOT re-resolve DNS itself,
     * so this code always means "the stored evidence says no", never "we could
     * not look".
     */
    DnsCheckRequired: "022603",
    /**
     * A `SUSPENDED_BOUNCE` account whose suspension is a confirmed HARD bounce
     * (`HARD_BOUNCE:*`), or one already flagged `permanentlyDisabled`. Neither
     * is lifted by a user Resume — the address is dead as a recipient — so the
     * remedy is support, not a retry. The worker's `resumeFromPause` refuses the
     * same cases (that copy is the one that holds); this code exists so the
     * click gets an error toast instead of a success toast over a silent no-op.
     */
    MailboxPermanentlyDisabled: "022604",
    /**
     * A partner-managed mailbox whose organisation's partner callback did not
     * answer the ping. Resume is an entry into a sending state, and the mailbox
     * holds no refresh token of its own — every token expiry is a call to that
     * partner — so resuming into a dead callback just walks the outage counter
     * back up to `SUSPENDED_PROVIDER` a few sends later while the UI reads
     * "warming". Advisory like every other gate on this endpoint (the api
     * writes no `accountState`); the automatic recovery sweep is the worker-side
     * path and pings the same host.
     *
     * Deliberately its own code: the remedy is "wait for the partner", which is
     * neither `NotUserResumable` (recovers on a schedule, nothing to retry) nor
     * `DnsCheckRequired` (the user fixes something).
     */
    PartnerUnreachable: "022605",
  },
  /** triggerVerification=27. */
  TriggerVerification: {
    FeatureNotAvailable: "022701",
    Disconnected: "022702",
    InProgress: "022703",
  },
  /** startBulkOperation=32 + getBulkJobStatus=32 (shared async bulk-job framework). */
  BulkOps: {
    OperationInvalid: "023201",
    EmptySelection: "023202",
    SelectionTooLarge: "023203",
    JobNotFound: "023204",
    MissingSettingsPatch: "023205",
  },
  /** disconnect=33. */
  Disconnect: {
    AlreadyDisconnected: "023301",
  },
  /** getSmtpConfig=30 — `GET /email-accounts/:id/smtp`, which returns the decrypted stored password(s). */
  GetSmtpConfig: {
    // The stored SMTP (or distinct IMAP) ciphertext is missing a part or fails
    // to decrypt under its `keyVersion`. Never answered with '' or a fallback:
    // an empty field would read as "no password set" and a save would then
    // re-probe with a blank password. The remedy is to re-enter it via PATCH.
    StoredPasswordUnreadable: "023001",
  },
  /** checkDns=36 — `POST /email-accounts/:id/dns/check` (the card's Re-check) + public v2 `setup-score/refresh`. */
  CheckDns: {
    // A manual Re-check is allowed 3h after the mailbox's last check of ANY
    // kind (connect, the daily health check, or an earlier Re-check).
    RecheckTooSoon: "023601",
    // A test-email run is still out for this mailbox.
    CheckInProgress: "023602",
  },
  /**
   * forceResumeWarmup=37 — internal, token-guarded
   * `POST /internal/email-accounts/:id/warmup/force-resume`. Not reachable by
   * any customer-facing route; the self-serve `resumeWarmup` keeps throwing
   * `ResumeWarmup.MailboxPermanentlyDisabled` for this same case.
   */
  InternalForceResume: {
    // Not `SUSPENDED_BOUNCE` on a confirmed hard-bounce reason
    // (`isHardBounceSuspendReason`). A rate-driven bounce suspension is not
    // eligible — it auto-resumes on its own.
    NotEligible: "023701",
  },
} as const;

export const EMAIL_ACCOUNTS_CATALOG: Record<string, CatalogEntry> = {
  // ── Shared (cross-endpoint) ──
  "020001": { httpStatus: 404, message: "We couldn't load your organization." },
  "020002": {
    httpStatus: 403,
    message:
      "You've reached your plan's email-account limit. Upgrade to add more.",
  },
  "020003": {
    httpStatus: 409,
    message: "That email is already connected to another inbox.",
  },
  "020004": {
    httpStatus: 404,
    message: "We couldn't find that email account.",
  },
  "020005": {
    httpStatus: 400,
    message:
      "SMTP settings can only be edited on password-connected accounts.",
  },
  "020006": {
    httpStatus: 409,
    message: "Please wait until the last email bulk action is completed.",
  },
  "020007": {
    httpStatus: 403,
    message:
      "You're already warming the maximum number of mailboxes your plan allows. Pause another mailbox or upgrade to warm more.",
  },
  "020008": {
    httpStatus: 403,
    // Names the domain-level reason without hinting at a bypass (no list, no
    // "try a subdomain"), and points at the one action that resolves it.
    message:
      "This email domain isn't supported. Please use a different domain, or contact support if you think this is a mistake.",
  },
  "020009": {
    httpStatus: 403,
    message:
      "Your subscription is paused. Resume your subscription to connect or reconnect mailboxes.",
  },

  // ── OAuth connect callbacks ──
  "020201": {
    httpStatus: 400,
    message:
      "Sign-in could not be completed. Please start connecting your account again.",
  },
  "020202": {
    httpStatus: 400,
    message:
      "This sign-in session expired. Please start connecting your account again.",
  },
  "020203": {
    httpStatus: 400,
    message: "We couldn't connect your account. Please try again.",
  },
  "020204": {
    httpStatus: 400,
    message:
      "We couldn't get the permissions we need from your provider. Please reconnect and allow offline access.",
  },
  "020206": {
    httpStatus: 400,
    message:
      "Your provider rejected this connection. Please reconnect and make sure you grant access when asked.",
  },
  "020205": {
    httpStatus: 400,
    message:
      "We couldn't read your mailbox profile. Please try connecting again.",
  },

  // ── Google Workspace admin ──
  // ── Partner headless OAuth connect (AA=35) ─────────────────────────────────
  "023501": {
    httpStatus: 403,
    message:
      "This workspace's organization has no verified partner integration. Register your callback endpoint before connecting mailboxes.",
  },
  "023502": {
    httpStatus: 400,
    message: "That provider cannot be connected with a partner-supplied OAuth token.",
  },
  "023503": {
    httpStatus: 422,
    message:
      "The access token belongs to a different mailbox than the email address you supplied.",
  },
  "023504": {
    httpStatus: 400,
    message: "The access token expires too soon. Send one valid for at least 5 more minutes.",
  },
  "020601": {
    httpStatus: 400,
    message:
      "Sign-in could not be completed. Please start the admin authorization again.",
  },
  "020701": {
    httpStatus: 400,
    message: "Authorize admin access before listing your domain mailboxes.",
  },
  "020702": {
    httpStatus: 400,
    message: "We couldn't list your domain mailboxes. Please try again.",
  },

  // ── SMTP / IMAP test + connect ──
  "021101": {
    httpStatus: 400,
    message: "We couldn't connect to your SMTP server.",
  },
  "021102": {
    httpStatus: 400,
    message: "We couldn't connect to your IMAP server.",
  },
  "021103": {
    httpStatus: 400,
    message:
      "That mail server address isn't publicly reachable. Use the hostname your provider gave you.",
  },
  "021201": {
    httpStatus: 400,
    message:
      "The email account you're connecting doesn't match the selected email service provider. Choose the correct provider, or use Custom SMTP / Other.",
  },

  // ── Bulk CSV connect ──
  "021301": { httpStatus: 400, message: "Please upload a .csv file." },
  "021302": {
    httpStatus: 400,
    message: "That file is too large. The maximum size is 20 MB.",
  },
  "021303": {
    httpStatus: 400,
    message: "That file has too many rows. The maximum is 10,000.",
  },
  "021304": {
    httpStatus: 404,
    message: "We couldn't find that import. It may have expired.",
  },
  "021305": {
    httpStatus: 400,
    message: "Upload your CSV as multipart/form-data.",
  },
  "021306": { httpStatus: 400, message: "Please attach a file to upload." },

  // ── Reconnect ──
  "022101": { httpStatus: 400, message: "This account is already connected." },
  "022102": {
    httpStatus: 400,
    message: "This account doesn't match the mailbox you're reconnecting.",
  },
  "022103": {
    httpStatus: 400,
    message: "Enter your SMTP/IMAP settings and password to reconnect this account.",
  },

  // ── Update warmup settings ──

  "022301": {
    httpStatus: 400,
    message: "Your sending window can't cross midnight.",
  },
  "022302": {
    httpStatus: 400,
    message: "Your sending window must be at least 2 hours.",
  },
  "022303": {
    httpStatus: 400,
    message: "Warmup volume can't exceed your plan's daily limit.",
  },
  "022304": {
    httpStatus: 400,
    message: "Your ESP percentages must add up to 100%.",
  },
  "022305": {
    httpStatus: 403,
    message: "Custom ESP distribution requires a plan upgrade.",
  },
  "022306": {
    httpStatus: 400,
    message:
      "Each active sending day must total at least 6 hours across its slots.",
  },
  "022307": {
    httpStatus: 400,
    message: "Your plan caps your warmup reply rate.",
  },
  "022308": {
    httpStatus: 403,
    message: "Custom warmup content requires a plan upgrade.",
  },
  "022309": {
    httpStatus: 403,
    message: "Warmup email templates require a plan upgrade.",
  },
  "022310": {
    httpStatus: 403,
    message: "A custom sending schedule requires a plan upgrade.",
  },
  "022311": {
    httpStatus: 403,
    message: "Choosing a warmup language requires a plan upgrade.",
  },
  "022312": {
    httpStatus: 409,
    message:
      "Your warmup schedule couldn't be saved due to a conflicting update - please try again.",
  },
  "022313": {
    httpStatus: 403,
    message: "Setting a warmup reply rate requires a plan upgrade.",
  },

  // ── Microsoft single-mailbox partner connect ──
  "023401": {
    httpStatus: 503,
    message:
      "Microsoft mailbox connect is not configured. Contact support.",
  },
  "023402": {
    httpStatus: 400,
    message:
      "That connection link is no longer valid. It may have expired or already been used - request a new one.",
  },
  "023403": {
    httpStatus: 403,
    message:
      "The Microsoft account that signed in does not match the mailbox this link was created for.",
  },
  "023404": {
    httpStatus: 400,
    message: "Microsoft sign-in was cancelled before the mailbox was connected.",
  },

  // ── Enable warmup ──
  "022401": {
    httpStatus: 400,
    message: "Your warmup max exceeds your plan's daily limit.",
  },
  "022402": {
    httpStatus: 400,
    message: "Reconnect this account before starting warmup.",
  },
  "022403": { httpStatus: 400, message: "Warmup is already running." },
  "022404": {
    httpStatus: 403,
    message:
      "Set up your SPF and MX records (plus DKIM for custom domains) before starting warmup.",
  },
  "022405": { httpStatus: 400, message: "Enable at least one sending day." },
  "022406": {
    httpStatus: 400,
    message: "This account is suspended. Resume it to continue warmup.",
  },
  "022407": {
    httpStatus: 409,
    message: "This account is still finishing reconnection. Try again in a moment.",
  },
  // 202-shaped in meaning but 409 in status: the request was understood and
  // acted on (a run was started), it just cannot complete now. The client polls
  // the account resource rather than retrying the enable.
  "022408": {
    httpStatus: 409,
    message:
      "We're checking that this mailbox can send and receive. Warmup starts automatically once it passes.",
  },
  "022409": {
    httpStatus: 400,
    message:
      "This mailbox failed our send-and-receive check. Fix the mailbox, then try again.",
  },

  // ── Pause warmup ──
  // TRU-1044: the code name is historical — the guard is now "not in a
  // manually-pausable state" (`MANUALLY_PAUSABLE_STATES`), which THROTTLED and
  // MAINTENANCE both satisfy. The old "isn't currently running" prose read as
  // "already paused" to users whose account was actively warming, which is what
  // made the bug so hard to recognise from the UI.
  "022501": {
    httpStatus: 400,
    message: "This mailbox's warmup is already paused or hasn't started.",
  },

  // ── Resume warmup ──
  "022601": {
    httpStatus: 400,
    message:
      "This account isn't paused or suspended, so there's nothing to resume.",
  },
  "022602": {
    httpStatus: 400,
    message:
      "This suspension clears on its own once the underlying issue recovers - it can't be resumed manually.",
  },
  "022603": {
    httpStatus: 403,
    message:
      "A required DNS record isn't passing. Fix your SPF and MX records (plus DKIM for custom domains), run Re-check DNS, then resume.",
  },
  "022604": {
    httpStatus: 400,
    message:
      "This mailbox's address bounced permanently, so warmup can't be resumed. Please contact our support team to get it unblocked.",
  },

  "022605": {
    httpStatus: 409,
    message:
      "Your provider callback isn't responding, so warmup can't resume yet. It resumes automatically once the callback answers - or try again in a few minutes.",
  },

  // ── Trigger verification ──
  "022701": {
    httpStatus: 403,
    message: "Inbox verification is not available on your plan.",
  },
  "022702": {
    httpStatus: 400,
    message: "Reconnect this account before running verification.",
  },
  "022703": {
    httpStatus: 409,
    message: "A verification run is already in progress for this account.",
  },

  // ── Bulk operations (async job framework) ──
  "023201": { httpStatus: 400, message: "That bulk action isn't supported." },
  "023202": { httpStatus: 400, message: "No accounts matched your selection." },
  "023203": {
    httpStatus: 400,
    message: "You can act on at most 10,000 accounts at once.",
  },
  "023204": { httpStatus: 404, message: "We couldn't find that bulk job." },
  "023205": {
    httpStatus: 400,
    message: "Choose at least one setting to apply.",
  },

  // ── Disconnect ──
  "023301": { httpStatus: 400, message: "This account is already disconnected." },

  // ── Get SMTP config ──
  // 409, like the other "the resource's current state blocks this" codes here
  // (022407, 022703): the request is well-formed, the stored row is the problem.
  "023001": {
    httpStatus: 409,
    message:
      "The stored password can't be read. Enter the password again to update it.",
  },

  // ── Check DNS (manual Re-check) ──
  "023601": {
    httpStatus: 429,
    message: "This mailbox was checked recently. Try again later.",
  },
  "023602": {
    httpStatus: 409,
    message: "A check is already running for this mailbox.",
  },
  "023701": {
    httpStatus: 409,
    message: "This mailbox isn't suspended on a hard bounce — nothing to force-resume.",
  },
};
